import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  LinearProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Snackbar,
  Stack,
  alpha,
  useTheme,
  Paper,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { passportService } from '../services/passportService';
import StatusChip from '../components/common/StatusChip';
import { DailySummaryRecord, ApplicationStatus } from '../types';

const DailySummary: React.FC = () => {
  const theme = useTheme();
  const { user, hasRole } = useAuth();
  const [records, setRecords] = useState<DailySummaryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Statistics
  const [totalVerifiedRecords, setTotalVerifiedRecords] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [searchParams, setSearchParams] = useState({
    status: '' as ApplicationStatus | '',
    user_name: '',
    from_date: getTodayDate(),
    to_date: getTodayDate(),
  });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      setHasSearched(true);

      const params = {
        ...searchParams,
        page,
        limit: 10,
      };

      const response = await passportService.getDailySummary(params as any);

      setRecords(response.results || []);
      setTotalRecords(response.count || 0);
      setTotalPages(Math.ceil((response.count || 0) / 10));
      
      // Update statistics
      setTotalVerifiedRecords(response.stats?.total_verified_records || 0);
      setTotalAmount(response.stats?.total_amount || 0);
    } catch (err: any) {
      console.error('Fetch error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch daily summary';
      setError(errorMessage);
      setRecords([]);
      setTotalVerifiedRecords(0);
      setTotalAmount(0);
    } finally {
      setLoading(false);
    }
  };

  // Calculate serial number based on current page
  const getSerialNumber = (index: number) => {
    return (page - 1) * 10 + index + 1;
  };

  const handleSearch = () => {
    setPage(1);
    fetchRecords();
  };

  const handleReset = () => {
    setSearchParams({
      status: '',
      user_name: '',
      from_date: getTodayDate(),
      to_date: getTodayDate(),
    });
    setPage(1);
    setRecords([]);
    setHasSearched(false);
    setTotalRecords(0);
    setTotalVerifiedRecords(0);
    setTotalAmount(0);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    const day = date.getDate();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  // Refetch when page changes
  useEffect(() => {
    if (hasSearched) {
      fetchRecords();
    }
  }, [page]);

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Daily Summary
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          View daily records summary and statistics
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              boxShadow: theme.shadows[2],
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: theme.shadows[6],
                transform: 'translateY(-4px)',
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                    Total Verified Records
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {totalVerifiedRecords.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {hasSearched ? 'Based on search criteria' : 'Today\'s verified records (by default)'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: alpha(theme.palette.success.main, 0.15),
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 36, color: 'success.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              boxShadow: theme.shadows[2],
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: theme.shadows[6],
                transform: 'translateY(-4px)',
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                    Total Amount (JD)
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {hasSearched ? 'Verified records amount' : 'Today\'s total amount (by default)'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: alpha(theme.palette.primary.main, 0.15),
                  }}
                >
                  <AttachMoneyIcon sx={{ fontSize: 36, color: 'primary.main' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Filters Card */}
      <Card
        sx={{
          mb: 3,
          boxShadow: theme.shadows[3],
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <FilterIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Search Filters
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={searchParams.from_date}
                onChange={(e) => setSearchParams({ ...searchParams, from_date: e.target.value })}
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={searchParams.to_date}
                onChange={(e) => setSearchParams({ ...searchParams, to_date: e.target.value })}
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={searchParams.status}
                  onChange={(e) => setSearchParams({ ...searchParams, status: e.target.value as ApplicationStatus | '' })}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="VERIFIED">Verified</MenuItem>
                  <MenuItem value="DELETED">Deleted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="User Name"
                value={searchParams.user_name}
                onChange={(e) => setSearchParams({ ...searchParams, user_name: e.target.value })}
                size="small"
                variant="outlined"
                placeholder="Created by user"
              />
            </Grid>

            {/* Action Buttons */}
            <Grid size={{ xs: 12 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  disabled={loading}
                  fullWidth
                  sx={{ minWidth: { sm: 150 } }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  disabled={loading}
                  fullWidth
                  sx={{ minWidth: { sm: 150 } }}
                >
                  Reset
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Alert */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Records Table Card */}
      <Card
        sx={{
          boxShadow: theme.shadows[4],
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Daily Records</span>
                {hasSearched && (
                  <Chip
                    label={`${totalRecords} records`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            </Typography>
            {loading && <LinearProgress sx={{ flex: 1, mx: 2, maxWidth: 300 }} />}
          </Box>

          {!hasSearched ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <AssessmentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Search Performed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click the "Search" button to view daily summary records
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 500px)', overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 700, width: 70, position: 'sticky', left: 0, bgcolor: alpha(theme.palette.primary.main, 0.05), zIndex: 2 }}>Sl. No.</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 100 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Passport No</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 110 }}>Mobile</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Enrolment No</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>Receipt No</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 100 }}>Amount (JD)</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 100 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Entry Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>Created At</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>Created By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {loading ? 'Loading daily summary...' : 'No records found matching the search criteria'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    records.map((record, index) => (
                      <TableRow
                        key={record.passport_applicants_id}
                        hover
                        sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                      >
                        <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {getSerialNumber(index)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {record.applicants_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={record.applicants_type_display}
                            size="small"
                            color={record.applicants_type === 'INDIVIDUAL' ? 'primary' : 'secondary'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{record.passport_no}</TableCell>
                        <TableCell>{record.mobile_number}</TableCell>
                        <TableCell>{record.enrolment_no}</TableCell>
                        <TableCell>{record.money_receipt_no || '—'}</TableCell>
                        <TableCell>
                          {record.amount_jd ? (
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                              {Number(record.amount_jd).toFixed(2)}
                            </Typography>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusChip status={record.current_status} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {formatDate(record.date_of_entry)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                            {formatDateTime(record.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {record.created_by}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          {hasSearched && totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DailySummary;